<template>
  <RCard class="attachments">
    <FormCreateAttachment
      :namespace="serviceNamespace"
      :showing="showForm"
      @refresh="getAttachments"
      @close="showForm = false"
    />

    <Grid gaf="column" justify-content="space-between" align-items="center">
      <div class="text-subtitle-1 text-secondary-2">Attachments</div>

      <PlusIcon @click.native="showNoteForm" />
    </Grid>

    <GridTable :columns="tableColumns" :rows="tableRows">
      <template #row="{row}">
        <GridTableCell v-if="row.isGroup" full-row class="text-center" no-tooltip>
          <strong>{{ row.data.entity | capitalize }} attachments </strong>
          <router-link :to="{ name: row.data.entity, params: { id: row.data.id } }" class="text-tertiary">
            ({{ row.data.entity_name }})
          </router-link>
        </GridTableCell>

        <template v-else>
          <GridTableCell no-tooltip>
            <span
              v-if="getFileType(row.url)"
              :class="`fiv-sqo fiv-size-md fiv-icon-${getFileType(row.url).toLowerCase()}`"
            />
            <span v-else class="fiv-sqo fiv-size-md fiv-icon-blank" />
          </GridTableCell>

          <GridTableCell>
            {{ row.name }}
          </GridTableCell>

          <GridTableCell class="grid items-center justify-center" no-tooltip>
            <LmTooltip>
              <div class="tooltip">
                <div class="row items-center q-pb-xs q-pt-xs">
                  <i class="fas fa-user on-left" />
                  <span>{{ row.created_by.name }}</span>
                </div>

                <div class="row items-center q-pb-xs q-pt-xs">
                  <i class="fas fa-clock on-left" />
                  <span class="on-left">
                    {{ formatDateForCustomDisplay(row.updated_at, 'D MMM. Y') }}
                  </span>
                </div>
              </div>
            </LmTooltip>
            <InfoIcon />
          </GridTableCell>

          <GridTableCell class="grid items-center justify-center" no-tooltip>
            <DotsIcon :popup-items="getPopupItems(row)" @popup-item="onPopupItem($event.name, row)" />
          </GridTableCell>
        </template>
      </template>
    </GridTable>
  </RCard>
</template>

<script>
import { DataService } from '@/api'
import * as apiTo from '@/modular-api'
// Components
import FormCreateAttachment from '@/components/forms/FormCreateAttachment'
import PlusIcon from '@/ui/icons/PlusIcon'
import InfoIcon from '@/ui/icons/InfoIcon'
import GridTable from '@/reusable/grid-table/GridTable'
import GridTableCell from '@/reusable/grid-table/GridTableCell'
import DotsIcon from '@/ui/icons/DotsIcon'

export default {
  name: 'LmAttachments',
  provide: function() {
    return {
      service: this.getService()
    }
  },
  components: {
    FormCreateAttachment,
    PlusIcon,
    InfoIcon,
    GridTable,
    GridTableCell,
    DotsIcon
  },
  props: {
    entity: {
      type: Object,
      required: true
    },
    storeNamespace: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showForm: false,
      attachmentsCollection: null
    }
  },
  computed: {
    tableColumns() {
      return [
        { label: 'Type', width: '42px' },
        { label: 'Name', align: 'left', width: '1fr' },
        { label: 'Info', width: '40px' },
        { label: 'Actions', width: '55px' }
      ]
    },

    tableRows() {
      if (!this.attachmentsCollection) return null

      return this.attachmentsCollection.reduce((acc, curr) => {
        const media = curr.media.map(media => ({ ...media, entity: curr.entity }))

        if (curr.entity !== this.entity.name) {
          acc.push({ isGroup: true, data: curr })
        }
        acc.push(...media)

        return acc
      }, [])
    },

    serviceNamespace() {
      return `${this.storeNamespace}/${this.entity.id}/files`
    }
  },

  created() {
    this.getAttachments()
  },

  methods: {
    showNoteForm() {
      this.showForm = true
    },

    getService() {
      return new DataService(this.serviceNamespace)
    },

    getAttachments() {
      const service = this.getService()
      return service
        .get({
          context: {
            grouped: true
          }
        })
        .then(response => (this.attachmentsCollection = response.data.result))
    },

    deleteAttachment(attachment) {
      const service = this.getService()
      return service.delete({ id: attachment.id }).then(() => {
        this.getAttachments()
      })
    },

    download(id) {
      apiTo.DOWNLOAD_MEDIA(id)
    },

    getPopupItems(row) {
      return [
        { name: 'download', label: 'Download' },
        { name: 'delete', label: 'Delete', textColorClass: 'text-danger-1', disable: row.entity !== this.entity.name }
      ]
    },

    onPopupItem(eventName, row) {
      if (eventName === 'delete') this.deleteAttachment(row)
      else if (eventName === 'download') this.download(row.id)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~file-icon-vectors/dist/file-icon-vectors.min.css';

.attachments {
  .fiv-sqo {
    line-height: inherit;
  }
}
</style>
